import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { groupCounselingService } from '../../../services/groupCounselingService';
import JitsiMeetRoom from '../../../components/JitsiMeetRoom';

const GroupSessionManager = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRegistrationsModal, setShowRegistrationsModal] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const defaultForm = {
    title: '',
    description: '',
    topic: '',
    sessionDate: '',
    sessionTime: '10:00',
    durationMinutes: 45,
    maxParticipants: 20,
    allowLateEntry: true,
    lateEntryMinutes: 10
  };
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (user?.id) loadSessions();
  }, [user?.id]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const data = await groupCounselingService?.getInstituteSessions(user?.id);
      setSessions(data || []);
    } catch (err) {
      console.error('Error loading group sessions:', err);
      setError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form?.title || !form?.sessionDate || !form?.sessionTime) {
      setError('Please fill in all required fields');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await groupCounselingService?.createSession({
        instituteId: user?.id,
        title: form?.title,
        description: form?.description,
        topic: form?.topic,
        scheduledAt: `${form?.sessionDate}T${form?.sessionTime}:00`,
        durationMinutes: parseInt(form?.durationMinutes),
        maxParticipants: parseInt(form?.maxParticipants),
        allowLateEntry: form?.allowLateEntry,
        lateEntryMinutes: parseInt(form?.lateEntryMinutes)
      });
      setForm(defaultForm);
      setShowCreateModal(false);
      await loadSessions();
    } catch (err) {
      console.error('Error creating session:', err);
      setError('Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (sessionId, newStatus) => {
    try {
      setSavingId(sessionId);
      await groupCounselingService?.updateSessionStatus(sessionId, newStatus);
      await loadSessions();
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setSavingId(null);
    }
  };

  const handleToggleLateEntry = async (session) => {
    try {
      setSavingId(session?.id);
      await groupCounselingService?.updateSession(session?.id, {
        title: session?.title,
        description: session?.description,
        topic: session?.topic,
        scheduledAt: session?.scheduled_at,
        durationMinutes: session?.duration_minutes,
        maxParticipants: session?.max_participants,
        allowLateEntry: !session?.allow_late_entry,
        lateEntryMinutes: session?.late_entry_minutes
      });
      await loadSessions();
    } catch (err) {
      console.error('Error toggling late entry:', err);
    } finally {
      setSavingId(null);
    }
  };

  const handleViewRegistrations = async (session) => {
    try {
      const data = await groupCounselingService?.getSessionRegistrations(session?.id);
      setRegistrations(data || []);
      setShowRegistrationsModal(session);
    } catch (err) {
      console.error('Error loading registrations:', err);
    }
  };

  const handleStartSession = (session) => {
    setActiveSession(session);
    handleStatusChange(session?.id, 'ongoing');
  };

  const getStatusBadge = (status) => {
    const map = {
      scheduled: 'bg-blue-100 text-blue-700',
      ongoing: 'bg-green-100 text-green-700',
      completed: 'bg-gray-100 text-gray-600',
      cancelled: 'bg-red-100 text-red-600'
    };
    return map?.[status] || 'bg-muted text-muted-foreground';
  };

  const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d?.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  if (activeSession) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" iconName="ArrowLeft" iconPosition="left"
            onClick={() => { setActiveSession(null); loadSessions(); }}>
            Back to Sessions
          </Button>
          <div>
            <h2 className="text-lg font-semibold text-foreground">{activeSession?.title}</h2>
            <p className="text-sm text-muted-foreground">Live Group Session</p>
          </div>
        </div>
        <JitsiMeetRoom
          roomName={activeSession?.jitsi_room_name}
          displayName={user?.name || 'Counselor'}
          title={activeSession?.title}
          onClose={() => { setActiveSession(null); loadSessions(); }}
          onLeave={() => { setActiveSession(null); handleStatusChange(activeSession?.id, 'completed'); }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground">Group Counseling Sessions</h2>
          <p className="text-sm text-muted-foreground mt-1">Create and manage one-to-many group sessions</p>
        </div>
        <Button iconName="Plus" iconPosition="left" onClick={() => setShowCreateModal(true)}>
          New Session
        </Button>
      </div>
      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-center gap-2">
          <Icon name="AlertCircle" size={16} color="var(--color-error)" />
          <span className="text-sm text-error">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto"><Icon name="X" size={14} /></button>
        </div>
      )}
      {/* Sessions List */}
      {loading && sessions?.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" />
        </div>
      ) : sessions?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="Users" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Group Sessions Yet</h3>
          <p className="text-muted-foreground mb-4">Create your first group counseling session to connect with multiple students at once.</p>
          <Button iconName="Plus" iconPosition="left" onClick={() => setShowCreateModal(true)}>Create Session</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {sessions?.map((session) => {
            const registeredCount = session?.registrations?.filter(r => r?.status === 'registered')?.length || 0;
            const isSaving = savingId === session?.id;
            return (
              <div key={session?.id} className="bg-card border border-border rounded-lg p-5">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-foreground">{session?.title}</h3>
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusBadge(session?.status)}`}>
                        {session?.status?.charAt(0)?.toUpperCase() + session?.status?.slice(1)}
                      </span>
                    </div>
                    {session?.topic && (
                      <p className="text-sm text-muted-foreground mb-1">📚 {session?.topic}</p>
                    )}
                    {session?.description && (
                      <p className="text-sm text-muted-foreground mb-2">{session?.description}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={14} />
                        {formatDateTime(session?.scheduled_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Clock" size={14} />
                        {session?.duration_minutes} min
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Users" size={14} />
                        {registeredCount} / {session?.max_participants} registered
                      </span>
                    </div>
                  </div>

                  {/* Late Entry Toggle */}
                  <div className="flex flex-col gap-2 min-w-[200px]">
                    <div className="flex items-center justify-between bg-muted rounded-lg px-3 py-2">
                      <div>
                        <p className="text-xs font-medium text-foreground">Late Entry</p>
                        <p className="text-xs text-muted-foreground">
                          {session?.allow_late_entry ? `+${session?.late_entry_minutes} min grace` : 'Disabled'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleToggleLateEntry(session)}
                        disabled={isSaving}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          session?.allow_late_entry ? 'bg-primary' : 'bg-muted-foreground/30'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          session?.allow_late_entry ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1"
                        onClick={() => handleViewRegistrations(session)}>
                        <Icon name="Users" size={14} className="mr-1" />
                        Registrations
                      </Button>
                      {session?.status === 'scheduled' && (
                        <Button size="sm" className="flex-1"
                          onClick={() => handleStartSession(session)}
                          disabled={isSaving}>
                          <Icon name="Video" size={14} className="mr-1" />
                          Start
                        </Button>
                      )}
                      {session?.status === 'ongoing' && (
                        <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={() => handleStartSession(session)}>
                          <Icon name="Video" size={14} className="mr-1" />
                          Rejoin
                        </Button>
                      )}
                      {(session?.status === 'scheduled' || session?.status === 'ongoing') && (
                        <Button variant="outline" size="sm"
                          onClick={() => handleStatusChange(session?.id, 'cancelled')}
                          disabled={isSaving}>
                          <Icon name="X" size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Create Session Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">Create Group Counseling Session</h3>
              <button onClick={() => { setShowCreateModal(false); setError(null); }}>
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {error && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-3 text-sm text-error">{error}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Session Title *</label>
                <input
                  type="text"
                  value={form?.title}
                  onChange={e => setForm(f => ({ ...f, title: e?.target?.value }))}
                  placeholder="e.g. MBA Admissions Q&A"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Topic</label>
                <input
                  type="text"
                  value={form?.topic}
                  onChange={e => setForm(f => ({ ...f, topic: e?.target?.value }))}
                  placeholder="e.g. Admissions, Scholarships, Campus Life"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                <textarea
                  value={form?.description}
                  onChange={e => setForm(f => ({ ...f, description: e?.target?.value }))}
                  rows={3}
                  placeholder="What will be covered in this session?"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Date *</label>
                  <input
                    type="date"
                    value={form?.sessionDate}
                    min={new Date()?.toISOString()?.split('T')?.[0]}
                    onChange={e => setForm(f => ({ ...f, sessionDate: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Time *</label>
                  <input
                    type="time"
                    value={form?.sessionTime}
                    onChange={e => setForm(f => ({ ...f, sessionTime: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Duration (minutes)</label>
                  <select
                    value={form?.durationMinutes}
                    onChange={e => setForm(f => ({ ...f, durationMinutes: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {[30, 45, 60, 90]?.map(d => <option key={d} value={d}>{d} min</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Max Participants</label>
                  <input
                    type="number"
                    min={2}
                    max={100}
                    value={form?.maxParticipants}
                    onChange={e => setForm(f => ({ ...f, maxParticipants: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              {/* Late Entry Settings */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">Allow Late Entry</p>
                    <p className="text-xs text-muted-foreground">Students can join after the session starts</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, allowLateEntry: !f?.allowLateEntry }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      form?.allowLateEntry ? 'bg-primary' : 'bg-muted-foreground/30'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      form?.allowLateEntry ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                {form?.allowLateEntry && (
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">
                      Grace Period (minutes after start)
                    </label>
                    <select
                      value={form?.lateEntryMinutes}
                      onChange={e => setForm(f => ({ ...f, lateEntryMinutes: e?.target?.value }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {[5, 10, 15, 20]?.map(m => <option key={m} value={m}>{m} minutes</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-border">
              <Button variant="outline" className="flex-1" onClick={() => { setShowCreateModal(false); setError(null); }}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleCreate} disabled={loading}>
                {loading ? 'Creating...' : 'Create Session'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Registrations Modal */}
      {showRegistrationsModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Registered Students</h3>
                <p className="text-sm text-muted-foreground">{showRegistrationsModal?.title}</p>
              </div>
              <button onClick={() => setShowRegistrationsModal(null)}>
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-6">
              {registrations?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No students registered yet</p>
              ) : (
                <div className="space-y-3">
                  {registrations?.map((reg) => (
                    <div key={reg?.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <Icon name="User" size={16} color="var(--color-primary)" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{reg?.student?.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{reg?.student?.email}</p>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(reg?.registered_at)?.toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupSessionManager;
